import React from 'react';
import { Home, ChevronRight, Volume2, Users, ArrowRight, ArrowLeft } from 'lucide-react';
import { ArchivalImageCard } from '../ArchivalImageCard';
import { useVoiceReader } from '../../context/VoiceReaderContext';

interface IsaacEphraimSectionProps {
  onSelectPart?: (partId: string) => void;
}

export const IsaacEphraimSection: React.FC<IsaacEphraimSectionProps> = ({ onSelectPart }) => {
  const { speakCurrentPage, isPlaying } = useVoiceReader();

  return (
    <div className="space-y-10 font-sans text-[#2c221e]">
      
      {/* Sub-navigation Breadcrumbs & Voice Narrator Controls */}
      <header className="bg-[#f3ede0] p-6 sm:p-8 md:p-10 rounded-3xl border-2 border-[#d8ceb9] shadow-sm space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3 text-xs font-serif text-[#7a5b3a]">
          
          {/* Breadcrumb: The Dentons > Part 6 of 9: Isaac Ephraim Denton */}
          <nav aria-label="Breadcrumb" className="flex flex-wrap items-center gap-1.5 sm:gap-2">
            <button
              onClick={() => onSelectPart && onSelectPart('hub')}
              className="hover:underline flex items-center gap-1 font-bold text-[#7a5b3a]"
            >
              <Home className="w-3.5 h-3.5" />
              <span>The Dentons</span>
            </button>
            <ChevronRight className="w-3.5 h-3.5 text-[#d8ceb9]" />
            <span className="bg-[#e8dec9] px-2.5 py-1 rounded-full font-bold uppercase tracking-wider text-[#7a5b3a] text-[11px] sm:text-xs">
              Part 6 of 9: Isaac Ephraim Denton
            </span>
          </nav>

          {/* Audio Narration Trigger */}
          <button
            onClick={speakCurrentPage}
            className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl bg-[#7a5b3a] hover:bg-[#5c4328] text-white text-xs font-bold transition shadow-xs cursor-pointer"
            title="Listen to Isaac Ephraim Denton"
          >
            <Volume2 className={`w-3.5 h-3.5 ${isPlaying ? 'animate-pulse text-amber-200' : 'text-amber-300'}`} />
            <span>{isPlaying ? 'Listening to Section...' : 'Listen to this Chapter'}</span>
          </button>
        </div>

        <h1 className="font-serif text-3xl md:text-5xl font-extrabold text-[#2c221e] tracking-tight leading-tight pt-1">
          ISAAC EPHRAIM DENTON (1863-1948)
        </h1>

        <div className="flex items-center gap-2 text-xs font-serif text-[#7a5b3a] uppercase tracking-wider font-semibold">
          <Users className="w-3.5 h-3.5" />
          <span>Part 6 of the 9-Part Denton Family History</span>
        </div>
      </header>

      {/* Main Archival Text Content */}
      <article className="bg-[#fbf9f4] p-6 sm:p-10 md:p-12 rounded-3xl border border-[#d8ceb9] shadow-sm space-y-10 text-base md:text-lg leading-relaxed text-[#2c221e]">
        
        {/* Section 1: The Southbridge Myth & Character */}
        <section className="space-y-6">
          <p>
            A Southbridge myth grew up around "Uncle Ike" as he grew older and his eccentricities developed,
            and it concerned an old-style leather satchel he carried everywhere with him. Somehow the notion
            took widespread belief that his satchel was full of money and his great niece Heather believed that
            was why he was forgiven many of his behaviours. It turned out that the satchel contained receipts
            going back nearly 40 years - "he didn't trust anyone much," Heather recalled.
          </p>

          <p>
            Isaac was the fourth of eight children to Frederick and Esther Denton of Fishley Farm, Eyreton. When
            he was 24 his father sold 300 ha of land at Loburn to Isaac and two of his brothers, Thomas and Bix.
            They sold that land three years later. Isaac bought 700 ha on the north side of the Waitaki River at
            Hakataramea, Takitu Homestead Run. He was engaged to Leith Giles but after he did not write to her
            for a very long time, she returned the ring when she next saw him and told him how displeased she
            was. <sup>1</sup>
          </p>

          <p>
            Three years later he was seriously injured when a horse fell on him, crushing his pelvis. He lay all
            night with his dogs keeping him warm before help came. He spent many months in hospital and was
            unable to carry on farming, so he sold up and moved to the Ellesmere district where his nephew
            Tommy Denton farmed at Oakdale. Isaac acquired three huts, one on the north Rakaia Huts, one at
            Selwyn Huts and a small squatter's hut at the top of Rakaia Island, near Oakdale. He spent the rest of
            his life moving between these huts, catching and smoking fish, tending his garden at Rakaia Huts and
            helping on family farms. He had a small wooden boat he fished from on the Selwyn River. The huts
            were small and modest but he drove, with pride, a 1911 Overland touring car, like this one pictured
            below. It was left to his great nephew Warren Denton and is now fully restored in private ownership.
          </p>

          {/* Image 90: 1911 Overland Touring car */}
          <div className="my-8">
            <ArchivalImageCard
              src="/images/90 1911 Ocverlander Touring car.jpg"
              alt="1911 Overland Touring car"
              caption="1911 Overland Touring car, like the one driven by Isaac Ephraim Denton and later restored."
              filenameHint="90 1911 Ocverlander Touring car.jpg"
            />
          </div>

          <p>
            Heather said if anyone in the family was a "character" it would have been Isaac. He often visited
            Tommy and Annie and would share his expertise on sheep and farming. She remembered that he
            had a refined voice and spoke very well, although he did not talk much socially. On the farm he had
            plenty to say and advise, where his opinions and knowledge were respected.
          </p>

          <p>
            He once went to stay with his niece Ivy and her husband Alf Gray who had a garage at Amberley. Ivy
            had just bought a nice deep sofa with a buttoned back. Isaac sat down in it, and rubbed his back
            backwards and forwards, but clearly unhappy about it he got out his pocketknife and cut the buttons
            off. "That's far better and a lot more comfortable," he told his horrified and very annoyed niece. Ivy
            was a fashionable and well-dressed woman, who grew freesias commercially for the Christchurch
            flower market. She had a taxi licence during and after the war so shared a love of cars with her
            uncle. But ruining her new sofa was a pocketknife too far.
          </p>

          <p>
            Isaac had an 1895 German Mauser 7x75 rifle from the Boer War (October 1899-May 1902) - he
            often showed the young children and he said he would go to the boulder bank at the Rakaia River
            mouth and shoot any invading Germans if they dared come to the coast.
          </p>

          <p>
            His sister Lucy Maria Morgan died aged 37. She had been a chronic asthmatic and suffered heart
            failure, leaving two children, Esther Lucy Thurza (9) and Rex Denton Morgan (7).
          </p>

          {/* Image 91: Lucy Maria Morgan */}
          <div className="my-8">
            <ArchivalImageCard
              src="/images/91 Lucy Maria Morgan.jpg"
              alt="Lucy Maria Morgan"
              caption="Lucy Maria Morgan"
              filenameHint="91 Lucy Maria Morgan.jpg"
            />
          </div>

          {/* Footnote 1 */}
          <div className="p-4 rounded-xl bg-[#e8dec9]/60 border border-[#d8ceb9] text-xs text-[#4a3525] italic my-6">
            <sup>1</sup> Leith had two sisters who had married Denton - Eliza Jane married John Frederick, Rebecca married (and later divorced) Albert Edward (Bix)
          </div>

          <p>
            Isaac provided financial support for her children's education and left them one quarter of his estate
            (the other beneficiaries were his brother-in-law Thomas Morgan, and nephews Tommy and Harry
            Denton).
          </p>

          {/* Image 92: Isaac Ephraim Denton signature */}
          <div className="my-8">
            <ArchivalImageCard
              src="/images/92 Isaac Ephraim Denton.png"
              alt="Isaac's signature"
              caption="Isaac's signature"
              filenameHint="92 Isaac Ephraim Denton.png"
            />
          </div>

          <p>
            Isaac is buried at Ellesmere Cemetery.
          </p>
        </section>

        {/* Section 2: Fishing in the Ellesmere District */}
        <section className="space-y-6 pt-6 border-t-2 border-[#d8ceb9]">
          <h2 className="font-serif text-2xl md:text-3xl font-bold text-[#2c221e]">
            FISHING IN THE ELLESMERE DISTRICT
          </h2>

          <p>
            Isaac chose his fishing spots well. The Selwyn Huts are alongside the Selwyn River and close to Te
            Waihora Lake Ellesmere.
          </p>

          {/* Image 93: Selwyn River */}
          <div className="my-8">
            <ArchivalImageCard
              src="/images/93 Selwyn River.jpg"
              alt="Selwyn River"
              caption="The Selwyn River"
              filenameHint="93 Selwyn River.jpg"
            />
          </div>

          <p>
            The Selwyn River longfin eels were famed for their size and the sheer number of them.
          </p>

          <blockquote className="p-6 rounded-2xl bg-[#f3ede0] border-l-4 border-[#7a5b3a] font-serif text-sm sm:text-base text-[#2c221e] italic leading-relaxed space-y-2">
            <p>
              In 1845 John Deans wrote: "There is a very large lake or lagoon on this plain; it is said to be 70 miles
              in circumference and I should say it is not much less; it is close to the sea beach and sometimes
              breaks through the sandbank that divides it from the sea. There are millions of eels in it; the natives
              make a sort of basket for catching them and they get tremendous quantities sometimes; they dry
              them in the sun and are almost the only food they have at times. In places like the lagoon and some
              of the still running rivers a good many of the eels are as thick as a man's leg and are very fat. We get
              them frequently and are very fond of them."<sup>2</sup>
            </p>
          </blockquote>

          {/* Image 94: Eeels Photo Ellesmere Te Waihora Trust */}
          <div className="my-8">
            <ArchivalImageCard
              src="/images/94 Eeels Photo Ellesmere Te Waihora Trust.jpg"
              alt="Ellesmere Longfin Eels"
              caption="Photo: Ellesmere Te Waihora Trust"
              filenameHint="94 Eeels Photo Ellesmere Te Waihora Trust.jpg"
            />
          </div>

          {/* Image 95: Te Waihora Lake Ellesmere */}
          <div className="my-8">
            <ArchivalImageCard
              src="/images/95 Te Waihora Lake Ellesmere.jpg"
              alt="Te Waihora Lake Ellesmere"
              caption="Te Waihora Lake Ellesmere"
              filenameHint="95 Te Waihora Lake Ellesmere.jpg"
            />
          </div>

          {/* Image 96: Rakaia River mouth */}
          <div className="my-8">
            <ArchivalImageCard
              src="/images/96 Rakaia River mouth.jpg"
              alt="Rakaia River mouth"
              caption="Rakaia River mouth - there are huts on both sides. Isaac's hut was on the north side. The Rakaia Island is between the main channels and part of it was farmed by Isaac's nephew Tommy Denton."
              filenameHint="96 Rakaia River mouth.jpg"
            />
          </div>

          {/* Footnote 2 */}
          <div className="p-4 rounded-xl bg-[#e8dec9]/60 border border-[#d8ceb9] text-xs text-[#4a3525] italic mt-6">
            <sup>2</sup> Pioneers of Canterbury Deans Letters, 1840-1852
          </div>
        </section>

        {/* Navigation Footer to Previous/Next Chapter */}
        <div className="pt-8 border-t-2 border-[#d8ceb9] flex flex-col sm:flex-row items-center justify-between gap-4">
          <button
            onClick={() => onSelectPart && onSelectPart('part5')}
            className="w-full sm:w-auto px-5 py-3 rounded-2xl bg-[#e8dec9] hover:bg-[#d8ceb9] text-[#2c221e] font-serif font-bold text-sm transition flex items-center justify-center gap-2 cursor-pointer shadow-xs"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Part 5: Young Annie Gibb</span>
          </button>

          <button
            onClick={() => onSelectPart && onSelectPart('part7')}
            className="w-full sm:w-auto px-5 py-3 rounded-2xl bg-[#7a5b3a] hover:bg-[#5c4328] text-white font-serif font-bold text-sm transition flex items-center justify-center gap-2 cursor-pointer shadow-xs"
          >
            <span>Part 7: Tommy Denton</span>
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>

      </article>

    </div>
  );
};
